import { assertOriginalRuleConfiguration } from '../utils/originalRuleConfiguration';
/**
 * ZeroToleranceRuleService - Handles zero-tolerance rule processing
 *
 * Zero-tolerance rules trigger immediate consequences when specific offenses occur
 */

import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  Timestamp,
  arrayUnion
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { getOffenceField } from '../utils/fieldNormalizer';
import { duplicateDetentionDetector } from '../../../utils/DuplicateDetentionDetector';
import { disciplineLogger } from '../../../utils/disciplineLogger';
import { consequenceTypesService } from '../../consequenceTypesService';
import { eventStyleConfig } from '../config/eventStyleConfig';
import NotificationService from '../../../features/notifications/services/NotificationService';
import { snapshotStaffIdentity } from '../utils/staffIdentity';
import { isPhaseExecutionError, phaseRuleAllowsRecord } from '../../domain/phaseRuntime';
import { isSchoolRulePeriodError } from '../utils/dateHelpers';

const FIRESTORE_IN_QUERY_LIMIT = 30;

function getMentionOffenseId(mention) {
  return mention?.offenceId || mention?.offenseId || null;
}

function normalizeOffenseName(value) {
  return typeof value === 'string'
    ? value.trim().replace(/\s+/g, ' ').toLowerCase()
    : '';
}

export class ZeroToleranceRuleService {
  constructor(dependencies) {
    this.ruleService = dependencies.ruleService;
    this.mentionService = dependencies.mentionService;
    this.consequenceService = dependencies.consequenceService;
  }

  /**
   * Process zero-tolerance rules for a mention
   * @param {Object} mention - The mention to process
   * @param {Object} studentData - Pre-fetched student data (unused but for consistency)
   * @returns {Promise<Array>} Consequences created by matching rules
   */
  async processZeroToleranceRules(mention, _studentData) {
    try {
      disciplineLogger.info(`Processing zero-tolerance rules for: ${getOffenceField(mention)}`, null, 'zeroTolerance');

      // Get all active zero-tolerance rules
      const activeRules = await this.ruleService.getActiveRules(mention.schoolId);
      const phasePolicy = await this.ruleService.getPhasePolicy?.(mention.schoolId);
      assertOriginalRuleConfiguration(mention.ruleConfiguration, activeRules, phasePolicy);
      const zeroToleranceRules = activeRules.filter(r => r.type === 'zero-tolerance');

      if (zeroToleranceRules.length === 0) {
        disciplineLogger.debug('No active zero-tolerance rules found', null, 'zeroTolerance');
        return [];
      }

      disciplineLogger.info(`Evaluating ${zeroToleranceRules.length} zero-tolerance rules`, null, 'zeroTolerance');

      const createdConsequences = [];
      for (const rule of zeroToleranceRules) {
        const consequence = await this.evaluateRule(rule, mention, phasePolicy);
        if (consequence) createdConsequences.push(consequence);
      }

      return createdConsequences;
    } catch (error) {
      if (error.ruleConfigurationChanged || isPhaseExecutionError(error) || isSchoolRulePeriodError(error)) throw error;
      console.error('Error processing zero-tolerance rules:', error);
      return [];
    }
  }

  /**
   * Evaluate a single zero-tolerance rule against a mention
   * @param {Object} rule - The rule to evaluate
   * @param {Object} mention - The mention to check
   * @returns {Promise<Object|null>} Created consequence when triggered
   */
  async evaluateRule(rule, mention, phasePolicy) {
    phasePolicy ??= await this.ruleService.getPhasePolicy?.(mention.schoolId);
    if (!phaseRuleAllowsRecord(phasePolicy, rule, mention)) return null;
    // Get offense IDs from the rule
    const offenseIds = rule.offenseTypes || rule.offenses || [];

    if (offenseIds.length === 0) {
      disciplineLogger.debug(`Rule "${rule.title}" has no configured offenses, skipping`, null, 'zeroTolerance');
      return null;
    }

    // Check if mention matches rule
    const matchFound = await this.checkOffenseMatch(mention, offenseIds, mention.schoolId);

    if (!matchFound) {
      return null;
    }

    disciplineLogger.critical(`Zero-tolerance rule triggered: ${rule.title}`, { offense: getOffenceField(mention) }, 'zeroTolerance');

    // Create the consequence
    return this.createConsequence(rule, mention);
  }

  /**
   * Check if mention's offense matches any of the rule's target offenses
   * @param {Object} mention - The mention to check
   * @param {Array} offenseIds - Offense IDs to match against
   * @param {string} schoolId - School ID (required for hierarchical collection)
   * @returns {Promise<boolean>} True if match found
   */
  async checkOffenseMatch(mention, offenseIds, schoolId) {
    // Separate "all-level-X" entries from real offense IDs
    const levelKeys = offenseIds.filter(id => /^all-level-\d+$/.test(id));
    const realOffenseIds = offenseIds.filter(id => !/^all-level-\d+$/.test(id));

    // Check "All Level X" matches against the level THIS mention was actually
    // issued at — not the offence's capability tag. An offence can declare
    // levels:[1,2] (it *can* be issued at 1 or 2), but a mention issued at
    // level 1 must NOT trip an all-level-2 rule. Match mention.level; fall back
    // to the offence's canonical single level only if the mention carries none.
    if (levelKeys.length > 0) {
      let issuedLevel = (mention.level !== undefined && mention.level !== null)
        ? Number(mention.level)
        : null;

      const mentionOffenseId = getMentionOffenseId(mention);
      if (issuedLevel === null && mentionOffenseId && schoolId) {
        try {
          const offenceSnap = await getDoc(doc(db, 'schools', schoolId, 'offences', mentionOffenseId));
          if (offenceSnap.exists()) {
            const offenceData = offenceSnap.data();
            issuedLevel = offenceData.level != null
              ? Number(offenceData.level)
              : (Array.isArray(offenceData.levels) && offenceData.levels.length > 0
                  ? Number(offenceData.levels[0])
                  : null);
          } else {
            disciplineLogger.warn(`Offence doc not found for level match: ${mentionOffenseId}`, null, 'zeroTolerance');
          }
        } catch (error) {
          disciplineLogger.warn('Error checking level match', { error: error.message }, 'zeroTolerance');
        }
      }

      if (issuedLevel !== null) {
        for (const key of levelKeys) {
          const targetLevel = parseInt(key.replace('all-level-', ''));
          if (issuedLevel === targetLevel) {
            disciplineLogger.debug(`Matched by level: mention issued at level ${issuedLevel}`, null, 'zeroTolerance');
            return true;
          }
        }
      }
    }

    // If no real offense IDs, we're done
    if (realOffenseIds.length === 0) {
      return false;
    }

    // A stored identity is authoritative. Current Mentions use the canonical
    // British spelling; older clients may still send the American spelling.
    // Do not fall back to names when an ID exists: separate offences can have
    // similar labels but materially different sanctions.
    const mentionOffenseId = getMentionOffenseId(mention);
    if (mentionOffenseId) {
      const matchedById = realOffenseIds.includes(mentionOffenseId);
      if (matchedById) {
        disciplineLogger.debug(`Matched by offense ID: ${mentionOffenseId}`, null, 'zeroTolerance');
      }
      return matchedById;
    }

    // CRITICAL: Validate schoolId for hierarchical collection
    if (!schoolId) {
      disciplineLogger.warn('Cannot check offense match: schoolId is missing for hierarchical offences collection', {
        mentionId: mention.id,
        offenseIds: realOffenseIds.length
      }, 'zeroTolerance');
      return false;
    }

    // Fall back to matching by offense name
    try {
      const offenseSnapshots = await Promise.all(
        Array.from(
          {length: Math.ceil(realOffenseIds.length / FIRESTORE_IN_QUERY_LIMIT)},
          (_, index) => realOffenseIds.slice(
            index * FIRESTORE_IN_QUERY_LIMIT,
            (index + 1) * FIRESTORE_IN_QUERY_LIMIT,
          ),
        ).map(ids => getDocs(query(
          collection(db, 'schools', schoolId, 'offences'),
          where('__name__', 'in', ids),
        ))),
      );
      const offenseNames = offenseSnapshots.flatMap(snapshot => snapshot.docs).map(doc =>
        doc.data().name || doc.data().offenseName || ''
      ).map(normalizeOffenseName).filter(Boolean);

      const mentionOffence = normalizeOffenseName(
        mention?.offence || mention?.offense || mention?.reason || mention?.offenseName
      );
      const matchFound = Boolean(mentionOffence) && offenseNames.includes(mentionOffence);

      if (matchFound) {
        disciplineLogger.debug(`Matched by offense name: "${getOffenceField(mention)}" matches one of [${offenseNames.join(', ')}]`, null, 'zeroTolerance');
      }

      return matchFound;
    } catch (error) {
      console.error('Error resolving offense IDs:', error);
      return false;
    }
  }

  /**
   * Create a consequence for a triggered zero-tolerance rule
   * @param {Object} rule - The triggered rule
   * @param {Object} mention - The triggering mention
   * @returns {Promise<Object|null>} Created consequence, or null for duplicate resolution
   */
  async createConsequence(rule, mention) {
    const consequenceType = rule.consequenceType || 'suspension';
    const offenseName = getOffenceField(mention);
    const studentFirstName = mention.studentName?.split(' ')[0] || 'Student';

    // Get proper consequence type label and singularize it
    const consequenceTypeDoc = await consequenceTypesService.getByValue(consequenceType, mention.schoolId);
    const consequenceLabelPlural = consequenceTypeDoc?.label || consequenceType;
    const consequenceLabel = eventStyleConfig.toSingular(consequenceLabelPlural);

    // Generate natural language description with article
    const article = /^[aeiou]/i.test(consequenceLabel) ? 'an' : 'a';
    const consequenceDetails = `${studentFirstName} received ${article} ${consequenceLabel} for ${offenseName}.`;

    const queueItem = {
      schoolId: mention.schoolId,
      studentId: mention.studentId,
      studentName: mention.studentName,
      className: mention.className,
      classId: mention.classId,
      type: consequenceType,
      status: 'pending',
      requiresAction: true,
      priority: 'urgent',
      severity: 'high',
      triggeredByRule: rule.id,
      ruleName: rule.title,
      ruleType: 'zero-tolerance',
      triggeredBy: [mention.id],
      triggeringMentions: [mention.id],
      consequenceDetails: consequenceDetails,
      notes: '',
      ...snapshotStaffIdentity(mention),
      createdBy: 'system-zero-tolerance',
      autoGenerated: true,
      offence: offenseName,
      undone: false
    };

    // Check for duplicate detentions before creating
    if (consequenceType === 'detention') {
      const duplicateCheck = await duplicateDetentionDetector.checkForDuplicateDetentions(queueItem);

      if (duplicateCheck.hasDuplicates) {
        disciplineLogger.info(`Duplicate detention detected for ${mention.studentName}`, { offense: getOffenceField(mention), duplicateCount: duplicateCheck.duplicateCount }, 'zeroTolerance');

        // Create resolution queue entry instead of the detention
        await duplicateDetentionDetector.createResolutionQueueEntry(queueItem, duplicateCheck.duplicates);

        // Update mention to note that consequence is pending resolution
        await this.mentionService.updateMention(mention.id, {
          schoolId: mention.schoolId,
          triggeredConsequences: [...(mention.triggeredConsequences || []), 'pending-resolution'],
          duplicateDetected: true,
          duplicateResolutionPending: true
        });

        return null;
      }
    }

    // Create the consequence
    const result = await this.consequenceService.createDisciplineConsequence(queueItem, {
      phaseSource: { collection: 'mentions', id: mention.id },
    });
    disciplineLogger.critical('Zero-tolerance consequence created', { id: result.id, type: consequenceType }, 'zeroTolerance');

    // Create notification for critical event
    try {
      await NotificationService.createNotification({
        type: 'rule',
        priority: 'critical',
        category: 'discipline',
        title: 'Zero-Tolerance Rule Triggered',
        message: `${mention.studentName} received ${article} ${consequenceLabel} for ${offenseName}`,
        metadata: {
          studentId: mention.studentId,
          studentName: mention.studentName,
          ruleId: rule.id,
          ruleTitle: rule.title,
          consequenceId: result.id,
          consequenceType: consequenceLabel,
          mentionId: mention.id,
          offenceName: offenseName,
          classId: mention.classId,
          className: mention.className,
        },
        schoolId: mention.schoolId,
        recipientIds: [], // Broadcast to school
        roleFilter: ['admin', 'staff'],
        createdById: mention.issuedById || 'system',
        actionable: true,
        actionUrl: `/students/${mention.studentId}`,
        actionLabel: 'View Student',
        expiresInDays: 30,
      });
    } catch (notificationError) {
      console.error('Failed to create notification for zero-tolerance rule:', notificationError);
      // Don't fail the whole operation if notification fails
    }

    // Update mention to mark it as having triggered a consequence
    await this.mentionService.updateMention(mention.id, {
      schoolId: mention.schoolId,
      triggeredConsequences: [...(mention.triggeredConsequences || []), result.id],
      triggeredZeroTolerance: true,
      actioned: true,
      actionedBy: arrayUnion(rule.id),
      actionedAt: Timestamp.now()
    });
    return result;
  }
}
